import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { ui } from '@/constants';
import { DietaryTagUI, MBV2NutritionSummary, MBV2SelectedItem, MonoMenuCacheItem, MonoMenuEntree, MonoMenuItem, MonoMenuItemCustomization, MonoMenuPreconfiguredMeal, MonoMenuTileItem } from '@/models';
import { getNutritionException } from '@/services/mono-menu/mono-menu-rules';
import { NutritionCalculatorPageLoadEvent } from '@/services/analytics/events';

@Component({
  name: 'CmgNutritionCalculatorMealBuilderCard',
})
export default class CmgNutritionCalculatorMealBuilderCard extends Vue {
  private types = {
    ENTREE: 'entree',
    ITEM: 'item',
    PCE: 'pce',
  };
  private includedItemType: string = 'includedItem';
  private defaultCustomization: MonoMenuItemCustomization = { id: null, name: 'Normal', countTowardsCustomizationMax: 0, countTowardsContentMax: 0, countTowardsOnTheSideCustomizationMax: 0 };

  @Prop({ required: true, type: Object })
  item?: MonoMenuTileItem;

  @Prop({ required: true, type: String })
  tileType?: string;

  get itemName(): string {
    if (this.tileType === this.types.ENTREE) {
      return (this.item as MonoMenuEntree).primaryFillingName;
    }

    return this.item?.itemName || '';
  }

  get thumbnailUrl(): string {
    return this.item?.thumbnailUrl || '';
  }

  get isSelected(): boolean {
    return !!this.item?.itemId && !!this.$appStore.state.mealBuilderV2.selectedItems[this.item.itemId];
  }

  get selectedItem(): MBV2SelectedItem | null {
    return !!this.item ? this.$appStore.state.mealBuilderV2.selectedItems[this.item.itemId] : null;
  }

  get isPortionModified(): boolean {
    return !!this.selectedItem?.quantity;
  }

  get portionModificationText(): string {
    return this.selectedItem?.quantity == 0.5 ? '1/2' : this.selectedItem?.quantity + 'X';
  }

  get hasDescription(): boolean {
    return !!this.item?.tags.length && !!this.item.tags.filter(t => t.type == 'tagline').length;
  }

  get description(): string {
    return this.item ? this.item.tags.filter(t => t.type == 'tagline')[0].value : '';
  }

  get showCustomizations(): boolean {
    return this.isSelected && this.hasCustomizations;
  }

  get hasCustomizations(): boolean {
    if (this.tileType === this.types.ENTREE) {
      const entree = this.item as MonoMenuEntree;
      return !!entree.customizations && entree.customizations.length > 0;
    }

    const menuItem = this.item as MonoMenuItem;
    return !!menuItem.customizations && menuItem.customizations.length > 0;
  }

  get customizations(): MonoMenuItemCustomization[] {
    // TODO: Getting portion errors of some sort on rice and beans. Fix this
    let allowedCustomizations: MonoMenuItemCustomization[] = [];
    if (this.tileType === this.types.ENTREE) {
      const entree = this.item as MonoMenuEntree;
      allowedCustomizations = entree.customizations ? allowedCustomizations.concat(entree.customizations) : allowedCustomizations;
    } else {
      const menuItem = this.item as MonoMenuItem;
      allowedCustomizations = menuItem.customizations ? allowedCustomizations.concat(menuItem.customizations) : allowedCustomizations;
    }

    return allowedCustomizations.filter(c => c.name !== 'Half');
  }

  get showDietaryTags(): boolean {
    return this.dietaryTags.length !== 0;
  }

  get getCheckedUrl(): string {
    return this.$appStore.state.cms.images.icons.checkedCircle.default;
  }

  get selectedCustomization(): MonoMenuItemCustomization {
    if (this.item) {
      const customization = this.$appStore.state.mealBuilderV2.selectedItems[this.item.itemId].customization;

      return this.customizations?.filter(c => c.id == (customization === 4 ? null : customization))[0];
    }

    return this.defaultCustomization;
  }

  get nutritionSections(): any[] {
    return this.$services.monoMenu.getTopLevelMenuNutritionDetailSections();
  }

  get isNoSelection(): boolean {
    return !!this.item?.thumbnailUrl && this.item?.thumbnailUrl?.indexOf('no-selection') != -1;
  }

  get isIncluded(): boolean {
    return this.tileType === this.includedItemType;
  }

  get dietaryTags(): DietaryTagUI[] {
    if (this.item) {
      const tagIntersection = this.item.dietaryTags?.filter(x => this.$appStore.state.customer.profile?.dietaryTags?.includes(x)) || [];

      // Only one badge from each group
      const tagBadges: {
        [key: string]: DietaryTagUI;
      } = {};
      tagIntersection
        .map(tag => (this.$services.monoMenu.getDietaryTagsLookup() as any)[tag])
        .filter(t => t != undefined)
        .forEach(t => (tagBadges[t!.tagGroupId] = t!));

      return Object.values(tagBadges);
    }

    return [];
  }

  get showValidationError(): boolean {
    return !!this.item && !!this.$appStore.state.mealBuilderV2.itemErrors[this.item.itemId];
  }

  get validationErrorMessage(): string {
    return !!this.item ? this.$appStore.state.mealBuilderV2.itemErrors[this.item.itemId] : '';
  }

  get isDesktop(): boolean {
    return this.$appStore.state.ui.clientWidth >= ui.breakpoints.desktop;
  }
  
  get isItemAvailable(): boolean {
    return !(this.item as MonoMenuItem)?.rules || (this.item as MonoMenuItem)?.rules.isItemAvailable as boolean;
  }

  get isDefaultContent(): Boolean | undefined {
    return (this.item as MonoMenuCacheItem).rules.defaultContent;
  }

  get hasEntree(): boolean {
    return this.tileType == this.types.PCE && (this.item as MonoMenuPreconfiguredMeal).hasEntree;
  }
  
  toggleSelected() {
    if (!!this.item) {
      if (this.isSelected) {
        this.remove();
      } else {
        this.upsert();
      }
    }
  }

  getNutrititionValue(key: keyof MBV2NutritionSummary): string {
    let nutritionValue: number;
    // Check to see if this menu item needs to display hard coded nutrition data for now
    const nutritionException = getNutritionException(this.item?.itemId, this.$appStore.state.mealBuilderV2.topLevelMenuItem?.title || null);
    if (nutritionException) {
      nutritionValue = nutritionException.nutrition[key];
    } else {
      nutritionValue = this.item ? this.item.nutrition[key] : -1;
    }
    const nutritionSection = this.nutritionSections?.find(x => x.key == key);
    let units: string = nutritionSection?.unit ? nutritionSection.unit : '';
    if (key === 'tcal') {
      units = this.$services.config.getEnergyUnitByCountry();
    }
    if (nutritionValue === undefined) {
      return '???';
    }

    let multiplier = this.$services.mealBuilderV2.getItemNutritionMultiplier(this.item?.itemId, null);
    
    return Math.round(nutritionValue * multiplier) + units;
  }

  upsert(customization: MonoMenuItemCustomization | null = null) {
    if (this.item?.itemId) {
      if (this.tileType === this.types.ENTREE && !customization) {
        this.$services.mealBuilderV2.upsertEntree(this.item.itemId, null);
      } else if (this.tileType === this.types.ENTREE && customization) {
        const selectedItem = Object.assign({}, this.$appStore.state.mealBuilderV2.selectedItems[this.item.itemId]);
        selectedItem.customization = customization.id || null;
        this.$services.mealBuilderV2.upsertEntree(this.item.itemId, selectedItem);
      } else if (this.tileType === this.types.ITEM && !customization) {
        this.$services.mealBuilderV2.upsertItem(this.item.itemId, null);
      } else if (this.tileType === this.types.ITEM && customization) {
        const selectedItem = Object.assign({}, this.$appStore.state.mealBuilderV2.selectedItems[this.item.itemId]);
        selectedItem.customization = customization.id;
        this.$services.mealBuilderV2.upsertItem(this.item.itemId, selectedItem);
      } else if (this.tileType === this.types.PCE) {
        this.$services.mealBuilderV2.upsertPCE(this.item.itemId);
      }
    }
  }

  remove() {
    if (this.item?.itemId) {
      if (this.tileType === this.types.ENTREE) {
        this.$services.mealBuilderV2.removeEntree(this.item.itemId);
      }
      if (this.tileType === this.types.ITEM) {
        if (this.isDefaultContent) {
          // Explicitly call remove default content to add this item to the list of opted out default item ids
          // That way, we don't keep spamming the user with a default item (i.e. guac, if they have told us they don't want it)
          this.$services.mealBuilderV2.removeDefaultContent(this.item.itemId)
        } else {
          this.$services.mealBuilderV2.removeItem(this.item.itemId);
        }
      }
      if (this.tileType === this.types.PCE) {
        this.$services.mealBuilderV2.removePCE(this.item.itemId);
      }
    }
  }

  onMakeItYourOwn() {
    this.$services.utils.setIsMakeItMyOwn(true);
    this.$services.mealBuilderV2.loadPreconfiguredMealToState(this.item!.itemId);
    // NC doesn't fire a page load event when a user chooses the 'Make It Your Own' option, so lets force one
    this.$services.analytics.send(new NutritionCalculatorPageLoadEvent('meal-builder', 'burrito-bowl'));
  }
}
